// Session Persistence Service - Handles browser focus/blur and prevents session loss
import { GameState } from '@/types';

interface SessionSnapshot {
  gameState: GameState;
  timestamp: number;
  isActive: boolean;
  sessionId: string;
}

class SessionPersistenceService {
  private currentSnapshot: SessionSnapshot | null = null;
  private isPageVisible = true;
  private visibilityChangeHandlers: ((isVisible: boolean) => void)[] = [];
  private beforeUnloadHandlers: (() => void)[] = [];
  private sessionRestoreHandlers: ((snapshot: SessionSnapshot) => void)[] = [];

  constructor() {
    this.initializeVisibilityHandling();
    this.initializeBeforeUnloadHandling();
  }

  private initializeVisibilityHandling(): void {
    // Handle page visibility changes (browser minimize, tab switch, etc.)
    document.addEventListener('visibilitychange', () => {
      const isVisible = !document.hidden;
      console.log('[SessionPersistence] Visibility changed:', isVisible ? 'visible' : 'hidden');
      
      this.isPageVisible = isVisible;
      
      if (!isVisible) {
        // Page is hidden - save current state
        this.handlePageHidden();
      } else {
        // Page is visible - restore state if needed
        this.handlePageVisible();
      }
      
      // Notify handlers
      this.visibilityChangeHandlers.forEach(handler => {
        try {
          handler(isVisible);
        } catch (error) {
          console.error('[SessionPersistence] Visibility handler error:', error);
        }
      });
    });

    // Handle window focus/blur as backup
    window.addEventListener('blur', () => {
      console.log('[SessionPersistence] Window blur');
      this.handlePageHidden();
    });

    window.addEventListener('focus', () => {
      console.log('[SessionPersistence] Window focus');
      this.handlePageVisible();
    });
  }

  private initializeBeforeUnloadHandling(): void {
    // Handle page unload (browser close, navigation, etc.)
    window.addEventListener('beforeunload', (event) => {
      console.log('[SessionPersistence] Before unload');
      
      // Save current state
      if (this.currentSnapshot) {
        this.saveSessionSnapshot(this.currentSnapshot);
      }
      
      // Notify handlers
      this.beforeUnloadHandlers.forEach(handler => {
        try {
          handler();
        } catch (error) {
          console.error('[SessionPersistence] Before unload handler error:', error);
        }
      });
    });
  }

  private handlePageHidden(): void {
    if (this.currentSnapshot) {
      console.log('[SessionPersistence] Saving session due to page hidden');
      this.saveSessionSnapshot({
        ...this.currentSnapshot,
        timestamp: Date.now(),
        isActive: false
      });
    }
  }

  private handlePageVisible(): void {
    console.log('[SessionPersistence] Page visible, checking for session restore');
    
    // Check if we need to restore a session
    const savedSnapshot = this.loadSessionSnapshot();
    if (savedSnapshot && this.shouldRestoreSession(savedSnapshot)) {
      console.log('[SessionPersistence] Restoring session from:', new Date(savedSnapshot.timestamp));
      
      // Notify restore handlers
      this.sessionRestoreHandlers.forEach(handler => {
        try {
          handler(savedSnapshot);
        } catch (error) {
          console.error('[SessionPersistence] Session restore handler error:', error);
        }
      });
    }
  }

  private shouldRestoreSession(snapshot: SessionSnapshot): boolean {
    // Don't restore if snapshot is too old (more than 1 hour)
    const maxAge = 60 * 60 * 1000; // 1 hour
    if (Date.now() - snapshot.timestamp > maxAge) {
      console.log('[SessionPersistence] Snapshot too old, not restoring');
      return false;
    }

    // Don't restore if we're already in a game session
    if (this.currentSnapshot && this.currentSnapshot.sessionId === snapshot.sessionId) {
      console.log('[SessionPersistence] Already in same session, not restoring');
      return false;
    }

    // Only restore if the snapshot was from a gameplay session
    if (snapshot.gameState.currentScreen !== 'gameplay') {
      console.log('[SessionPersistence] Snapshot not from gameplay, not restoring');
      return false;
    }

    return true;
  }

  // Public API
  public updateCurrentSession(gameState: GameState): void {
    this.currentSnapshot = {
      gameState: { ...gameState },
      timestamp: Date.now(),
      isActive: this.isPageVisible,
      sessionId: gameState.currentSessionId || `session_${Date.now()}`
    };
  }

  public saveSessionSnapshot(snapshot: SessionSnapshot): void {
    try {
      const key = 'lucidCyberRPG_sessionSnapshot';
      localStorage.setItem(key, JSON.stringify(snapshot));
      console.log('[SessionPersistence] Session snapshot saved');
    } catch (error) {
      console.error('[SessionPersistence] Failed to save session snapshot:', error);
    }
  }

  public loadSessionSnapshot(): SessionSnapshot | null {
    try {
      const key = 'lucidCyberRPG_sessionSnapshot';
      const saved = localStorage.getItem(key);
      if (saved) {
        const snapshot = JSON.parse(saved);
        console.log('[SessionPersistence] Session snapshot loaded');
        return snapshot;
      }
    } catch (error) {
      console.error('[SessionPersistence] Failed to load session snapshot:', error);
    }
    return null;
  }

  public clearSessionSnapshot(): void {
    try {
      const key = 'lucidCyberRPG_sessionSnapshot';
      localStorage.removeItem(key);
      console.log('[SessionPersistence] Session snapshot cleared');
    } catch (error) {
      console.error('[SessionPersistence] Failed to clear session snapshot:', error);
    }
  }

  public isPageCurrentlyVisible(): boolean {
    return this.isPageVisible;
  }

  public getCurrentSnapshot(): SessionSnapshot | null {
    return this.currentSnapshot;
  }

  // Event handlers
  public onVisibilityChange(handler: (isVisible: boolean) => void): void {
    this.visibilityChangeHandlers.push(handler);
  }

  public onBeforeUnload(handler: () => void): void {
    this.beforeUnloadHandlers.push(handler);
  }

  public onSessionRestore(handler: (snapshot: SessionSnapshot) => void): void {
    this.sessionRestoreHandlers.push(handler);
  }

  public removeVisibilityHandler(handler: (isVisible: boolean) => void): void {
    const index = this.visibilityChangeHandlers.indexOf(handler);
    if (index > -1) {
      this.visibilityChangeHandlers.splice(index, 1);
    }
  }

  public removeBeforeUnloadHandler(handler: () => void): void {
    const index = this.beforeUnloadHandlers.indexOf(handler);
    if (index > -1) {
      this.beforeUnloadHandlers.splice(index, 1);
    }
  }

  public removeSessionRestoreHandler(handler: (snapshot: SessionSnapshot) => void): void {
    const index = this.sessionRestoreHandlers.indexOf(handler);
    if (index > -1) {
      this.sessionRestoreHandlers.splice(index, 1);
    }
  }

  // Utility methods
  public createSessionContinueDialog(): {
    show: boolean;
    message: string;
    onContinue: () => void;
    onRestart: () => void;
  } | null {
    const snapshot = this.loadSessionSnapshot();
    if (!snapshot || !this.shouldRestoreSession(snapshot)) {
      return null;
    }

    const timeSince = Date.now() - snapshot.timestamp;
    const minutesAgo = Math.floor(timeSince / (1000 * 60));
    
    return {
      show: true,
      message: `You have a saved session from ${minutesAgo} minutes ago. Would you like to continue where you left off?`,
      onContinue: () => {
        this.sessionRestoreHandlers.forEach(handler => handler(snapshot));
        this.clearSessionSnapshot();
      },
      onRestart: () => {
        this.clearSessionSnapshot();
      }
    };
  }
}

// Export singleton instance
export const sessionPersistenceService = new SessionPersistenceService();